<template>
    <div class="schedule-container">
        <!-- 헤더: 진료 시간 타이틀 + 확장 화살표 -->
        <div class="schedule-header" @click="toggleExpand">
            <div class="title-info-row">
                <span class="title">예약 가능 시간</span>
                <div class="tooltip-wrapper">
                    <img style="width: 20px; height: 20px; display: block; cursor: pointer;" src="../../assets/info.png" @click.stop="showTooltip = !showTooltip" />
                    <div class="tooltip" v-if="showTooltip">
                        <span class="tooltip-text">솔닥 앱에서 예약할 수 있는 시간이에요.</span>
                        <div class="tooltip-arrow"></div>
                    </div>
                </div>
            </div>

            <div class="expand-icon" :class="{ expanded: isExpanded }">
                <svg width="20" height="20" viewBox="0 0 20 20">
                    <path d="M4 7L10 13L16 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </div>
        </div>
        <!-- 오늘 스케줄 표시 -->
        <div class="schedule-today">
            <div class="today-info">
                <span v-if="todaySchedule && todaySchedule.is_open" class="status-badge open">진료중</span>
                <span v-else class="status-badge closed">진료종료</span>
                <span class="day-time">
                    {{ todayDayName }} | {{ todayTimeRange }}
                </span>
            </div>
        </div>

        <!-- 확장된 스케줄 목록 -->
        <transition name="expand">
            <div v-if="isExpanded" class="schedule-list">
                <div
                    v-for="schedule in sortedSchedules"
                    :key="schedule.day_of_week"
                    class="schedule-item"
                    :class="{ 'is-today': isToday(schedule.day_of_week) }"
                >
                    <span class="day-name">{{ getDayName(schedule.day_of_week) }}</span>
                    <span v-if="schedule.is_open" class="time-range">
                        {{ getTimeRange(schedule) }}
                    </span>
                    <span v-else class="closed-text">휴진</span>
                </div>
            </div>
        </transition>
        <span class="info-text">* 실제 병원의 운영 시간과 다를 수 있으니, 방문하시는 고객님께서는 병원에 문의해 주시기 바랍니다.</span>
    </div>
</template>

<script>
export default {
    name: 'schedule',
    props: {
        schedules: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            isExpanded: false,
            showTooltip: false,
            dayNames: ['일', '월', '화', '수', '목', '금', '토']
        }
    },
    mounted() {
        this._onClickOutside = (e) => {
            if (this.showTooltip && !this.$el.querySelector('.tooltip-wrapper').contains(e.target)) {
                this.showTooltip = false;
            }
        };
        document.addEventListener('click', this._onClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener('click', this._onClickOutside);
    },
    watch: {
        schedules: {
            immediate: true,
            handler() {
                this.$nextTick(() => {
                    this.emitServiceTypes()
                })
            }
        }
    },
    computed: {
        todayDayOfWeek() {
            return new Date().getDay()
        },
        todaySchedule() {
            if (!this.schedules || this.schedules.length === 0) return null
            return this.schedules.find(s => s.day_of_week === this.todayDayOfWeek)
        },
        todayDayName() {
            return this.dayNames[this.todayDayOfWeek]
        },
        todayTimeRange() {
            if (!this.todaySchedule || !this.todaySchedule.is_open) return '휴진'
            return this.getTimeRange(this.todaySchedule)
        },
        sortedSchedules() {
            if (!this.schedules) return []
            // 월요일(1)부터 일요일(0) 순서로 정렬
            // 일요일(0)을 7로 변환하여 마지막에 오도록 함
            return [...this.schedules].sort((a, b) => {
                const aOrder = a.day_of_week === 0 ? 7 : a.day_of_week
                const bOrder = b.day_of_week === 0 ? 7 : b.day_of_week
                return aOrder - bOrder
            })
        },
        hasContactSchedule() {
            if (!this.schedules) return false
            return this.schedules.some(s =>
                s.base_hospital_schedule_time &&
                s.base_hospital_schedule_time.some(t => t.contact_number > 0)
            )
        },
        hasContactlessSchedule() {
            if (!this.schedules) return false
            return this.schedules.some(s =>
                s.base_hospital_schedule_time &&
                s.base_hospital_schedule_time.some(t => t.contactless_number > 0)
            )
        }
    },
    methods: {
        toggleExpand() {
            this.isExpanded = !this.isExpanded
        },
        getDayName(dayOfWeek) {
            return this.dayNames[dayOfWeek]
        },
        isToday(dayOfWeek) {
            return dayOfWeek === this.todayDayOfWeek
        },
        getTimeRange(schedule) {
            if (!schedule.base_hospital_schedule_time || schedule.base_hospital_schedule_time.length === 0) {
                return '시간 정보 없음'
            }
            const times = schedule.base_hospital_schedule_time
            const startTimes = times.map(t => t.start_time).sort()
            const endTimes = times.map(t => t.end_time).sort()
            const earliest = startTimes[0]
            const latest = endTimes[endTimes.length - 1]
            return `${earliest} ~ ${latest}`
        },
        emitServiceTypes() {
            this.$emit('service-types', {
                hasContact: this.hasContactSchedule,
                hasContactless: this.hasContactlessSchedule
            })
        }
    }
}
</script>

<style scoped>
.schedule-container {
    padding: 20px 16px;
    width: 100%;
    border-radius: 6px;
    box-sizing: border-box;
    background: var(--gray-00, #FFF);
    box-shadow: 0 0 10px 0 rgba(0, 33, 56, 0.09);
    margin-bottom: 16px;
    margin-top: 16px;
    font-family: Pretendard;
}

.schedule-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    margin-bottom: 12px;
}

.schedule-today {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.today-info {
    display: flex;
    align-items: center;
    gap: 8px;
}

.today-label {
    font-weight: 600;
    color: #333;
}

.status-badge {
    padding: 2px 8px;
    border-radius: 4px;
    font-family: Pretendard;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
}

.status-badge.open {
    background: #E5F0FC;
    color: var(--Primary-100, #008DEF);
}

.status-badge.closed {
    background: #fafafa;
    color: #999;
}

.day-time {
    color: #666;
    font-size: 14px;
}

.expand-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s ease;
    color: #666;
}

.expand-icon.expanded {
    transform: rotate(180deg);
}

.service-types {
    display: flex;
    gap: 8px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
}

.service-badge {
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 500;
}

.service-badge.contact {
    background: #e3f2fd;
    color: #1976d2;
}

.service-badge.contactless {
    background: #f3e5f5;
    color: #7b1fa2;
}

.schedule-list {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
}

.schedule-item {
    display: flex;
    align-items: center;
    padding: 4px 0;
    gap: 8px;
}

.schedule-item.is-today {
    background: #f5f5f5;
    margin: 0 -16px;
    padding: 8px 16px;
    border-radius: 4px;
}

.day-name {
    width: 14px;
    font-weight: 500;
    color: #666;
    font-size: 14px;
}

.time-range {
    color: #666;
    font-weight: 500;
    font-size: 14px;
}

.closed-text {
    color: #999;
    font-size: 14px;
}

.expand-enter-active,
.expand-leave-active {
    transition: all 0.3s ease;
    overflow: hidden;
}

.expand-enter,
.expand-leave-to {
    opacity: 0;
    max-height: 0;
}

.expand-enter-to,
.expand-leave {
    opacity: 1;
    max-height: 500px;
}
.title {
    color: var(--Gray-100, #1E1E1E);
    font-family: Pretendard;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px; /* 125% */
}
.info-text {
    color: var(--Gray-50, #A3AAB6);
    font-family: Pretendard;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px; /* 133.333% */
    margin-top: 8px;
}
.title-info-row {
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 4px;
}
.tooltip-wrapper {
    position: relative;
}
.tooltip {
    position: absolute;
    top: 30px;
    left: 0;
    transform: translateX(-10%);
    border-radius: 6px;
    background: var(--Primary-20, #E5F0FC);
    display: inline-flex;
    padding: 8px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    white-space: nowrap;
    z-index: 10;
}
.tooltip-arrow {
    position: absolute;
    top: -6px;
    left: 10%;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid var(--Primary-20, #E5F0FC);
}
.tooltip-text {
    color: var(--color-wb-black, #000);

    /* body/XXS/medium */
    font-family: Pretendard;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 150%; /* 18px */
}
</style>


// WEBPACK FOOTER //
// src/components/hospital/Schedule.vue